// Instrumente-Mathe (AUFTRAG K3, docs/INSTRUMENTE-KATALOG.md I1–I3).
//
// Pure functions — die SVG-Komponenten rendern nur, gerechnet wird hier
// (vitest-bar, kein React-Import).

export const ZIEGEL_WERT_EUR = 10_000; // 1 Ziegel = 10k Jahresüberschuss (I2)

/** Umsatz-Pace vs. Jahresziel aus dem Vorjahr (I3: Drehzahl der Räder).
 *
 * ratio = YTD / (VJ-Gesamt × Tag-im-Jahr). 1.0 = exakt auf VJ-Kurs,
 * >1 = schneller. null wenn eine Seite fehlt (Rad steht, "Aufzug fehlt").
 */
export function paceRatio(
  ytdEur: number | null,
  vjGesamtEur: number | null,
  tagImJahrPct: number,
): number | null {
  if (ytdEur == null || vjGesamtEur == null || vjGesamtEur <= 0 || tagImJahrPct <= 0) {
    return null;
  }
  return ytdEur / (vjGesamtEur * tagImJahrPct);
}

/** Sediment-Dicke 0..1 (I1: Ablagerung am Rohrboden = überfällige €).
 *  Skala bis 50k (Manometer-Schwelle — Kohärenz mit dem Lage-Score). */
export function sedimentAnteil(ueberfaelligEur: number | null, maxEur = 50_000): number {
  if (ueberfaelligEur == null || ueberfaelligEur <= 0) return 0;
  return Math.min(1, ueberfaelligEur / maxEur);
}

/** Rohr-Durchmesser (DN) aus Umsatz — I1: Durchmesser = Umsatz, skaliert
 *  auf 14–34 px zwischen min/max der Zuläufe. */
export function dnFor(
  umsatzEur: number | null,
  alleUmsaetze: (number | null)[],
): number {
  const werte = alleUmsaetze.filter((v): v is number => v != null && v > 0);
  if (umsatzEur == null || umsatzEur <= 0 || werte.length === 0) return 14;
  const min = Math.min(...werte);
  const max = Math.max(...werte);
  if (max === min) return 24;
  return 14 + ((umsatzEur - min) / (max - min)) * 20;
}

/** Flow-Partikel-Dauer in Sekunden (I1: Tempo = DSO — hoher DSO = zäher
 *  Fluss). 20 Tage → 2.5s, 90 Tage → 8s, geklemmt. */
export function flowDauerSekunden(dsoDays: number | null): number {
  if (dsoDays == null || dsoDays <= 0) return 4;
  return Math.min(8, Math.max(2.5, 2.5 + ((dsoDays - 20) / 70) * 5.5));
}

export interface JahresSchicht {
  jahr: number;
  jueEur: number;
  ziegel: number;
  festgestellt: boolean;
}

/** Jahres-Schichten des Fundaments (I2): JÜ der Gruppe pro Jahr → Ziegel.
 *
 * eintraege = ja_seed-Werte (mehrere Firmen pro Jahr → Summe). laufend =
 * vorläufiges Konzern-Ergebnis YTD (BWA) als oberste, unfeste Schicht.
 */
export function jahresSchichten(
  eintraege: { jahr: number; jahresueberschuss_eur: string | null; festgestellt_am: string | null }[],
  laufendJahr: number,
  laufendEur: number | null,
): JahresSchicht[] {
  const proJahr = new Map<number, { summe: number; fest: boolean }>();
  for (const e of eintraege) {
    const v = e.jahresueberschuss_eur == null ? null : Number.parseFloat(e.jahresueberschuss_eur);
    if (v == null || !Number.isFinite(v)) continue;
    const cur = proJahr.get(e.jahr) ?? { summe: 0, fest: false };
    cur.summe += v;
    cur.fest = cur.fest || e.festgestellt_am != null;
    proJahr.set(e.jahr, cur);
  }
  const schichten: JahresSchicht[] = [...proJahr.entries()]
    .map(([jahr, { summe, fest }]) => ({
      jahr,
      jueEur: summe,
      ziegel: Math.max(0, Math.round(summe / ZIEGEL_WERT_EUR)),
      festgestellt: fest,
    }))
    .sort((a, b) => a.jahr - b.jahr);

  if (laufendEur != null && laufendEur > 0 && !proJahr.has(laufendJahr)) {
    schichten.push({
      jahr: laufendJahr,
      jueEur: laufendEur,
      ziegel: Math.max(0, Math.round(laufendEur / ZIEGEL_WERT_EUR)),
      festgestellt: false,
    });
  }
  return schichten;
}
